// eslint-disable-next-line jira/restricted/react
import React, { useRef, useEffect, PureComponent } from 'react';
import { styled as styled2 } from '@compiled/react';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import { token } from '@atlaskit/tokens';
import { Stack } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { AnnouncerV2 } from '@atlassian/jira-accessibility/src/ui/announcer-v2/index.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { defineMessages, useIntl } from '@atlassian/jira-intl';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { ASC } from '../../common/constants/order-directions.tsx';
import { NAME, DESCRIPTION, LEAD, ISSUES } from '../../common/constants/order-fields.tsx';
import type { OrderField, OrderDirection } from '../../query-param-adapter/model/order.tsx';
import ActionsCell from './cell/actions/index.tsx';
import ComponentCell from './cell/component/index.tsx';
import DefaultAssigneeCell from './cell/default-assignee/index.tsx';
import DescriptionCell from './cell/description/index.tsx';
import IssueCountCell from './cell/issues/index.tsx';
import LeadCell from './cell/lead/index.tsx';
import EmptyView from './empty-view/view.tsx';

const messages = defineMessages({
	component: {
		id: 'project-components.table-header-component',
		defaultMessage: 'Component',
		description: '',
	},
	issues: {
		id: 'project-components.table-header-issues',
		defaultMessage: 'Issues',
		description: '',
	},
	lead: {
		id: 'project-components.table-header-lead',
		defaultMessage: 'Component lead',
		description: '',
	},
	description: {
		id: 'project-components.table-header-description',
		defaultMessage: 'Description',
		description: '',
	},
	defaultAssignee: {
		id: 'project-components.table-header-default-assignee',
		defaultMessage: 'Default assignee',
		description: '',
	},
	moreActions: {
		id: 'project-components.table-header-more-actions',
		defaultMessage: 'More actions',
		description: '',
	},
	componentsTableLabel: {
		id: 'project-components.table-components-label',
		defaultMessage: 'Table of components',
		description: 'Aria label text for table with list of components',
	},
	searchResults: {
		id: 'project-components.table-search-results',
		defaultMessage:
			'{total, plural, =0 {No suggestions available for typed text} one {# suggestion available for typed text} other {# suggestions available for typed text}}',
		description: 'Message to announce search results when filtering components',
	},
});

export type Props = {
	intl: IntlShape;
	editable: boolean;
	isLoading: boolean;
	orderField: OrderField;
	orderDirection: OrderDirection;
	componentIds: string[];
	onSort?: (sort: { key: OrderField; sortOrder: OrderDirection }) => void;
	query?: string;
};

export type PropsNew = {
	editable: boolean;
	isLoading: boolean;
	orderField: OrderField;
	orderDirection: OrderDirection;
	componentIds: string[];
	onSort?: (sort: { key: OrderField; sortOrder: OrderDirection }) => void;
	query?: string;
};

// eslint-disable-next-line jira/react/no-class-components
class TableViewOld extends PureComponent<Props> {
	static defaultProps: Partial<Props> = {
		editable: false,
		isLoading: false,
		orderField: NAME,
		orderDirection: ASC,
	};

	render() {
		const { isLoading, componentIds, editable, onSort, orderField, orderDirection, intl } =
			this.props;

		const { formatMessage } = intl;
		const actionCellHeader = (
			// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
			<span data-test-id="project-directories.components.table.action-cell-header-container">
				{formatMessage(messages.moreActions)}
			</span>
		);

		const headCells = [
			{
				key: NAME,
				content: formatMessage(messages.component),
				width: 20,
				isSortable: true,
			},
			{
				key: DESCRIPTION,
				content: formatMessage(messages.description),
				width: 30,
				isSortable: true,
			},
			{ key: LEAD, content: formatMessage(messages.lead), width: 20, isSortable: true },
			{ content: formatMessage(messages.defaultAssignee), width: 15 },
			{
				key: ISSUES,
				content: formatMessage(messages.issues),
				width: 10,
				isSortable: true,
			},
			{
				content: actionCellHeader,
				width: 6,
			},
		];

		const head = { cells: editable ? headCells : headCells.slice(0, headCells.length - 1) };
		const rowCells = componentIds.map((componentId, index) => {
			// the page is already sorted (by the API), so fake key,
			// so that DynamicTable won't destroy it
			const key = orderDirection === 'DESC' ? -(index + 1) : index + 1;
			const cells = [
				{
					content: (
						<ComponentCellContainer>
							<ComponentCell componentId={componentId} />
						</ComponentCellContainer>
					),
					key,
				},
				{ content: <DescriptionCell componentId={componentId} />, key },
				{ content: <LeadCell componentId={componentId} />, key },
				{ content: <DefaultAssigneeCell componentId={componentId} />, key },
				{ content: <IssueCountCell componentId={componentId} />, key },
			];
			if (editable) {
				// @ts-expect-error - TS2345 - Argument of type '{ content: JSX.Element; }' is not assignable to parameter of type '{ content: JSX.Element; key: number; }'.
				cells.push({ content: <ActionsCell componentId={componentId} /> });
			}
			return { cells };
		});

		return (
			<TableContainer data-test-id="project-directories.components.table.table-container">
				<DynamicTableStateless
					isFixedSize
					head={head}
					rows={rowCells}
					onSort={onSort}
					sortKey={orderField}
					sortOrder={orderDirection}
					isLoading={isLoading}
					emptyView={isLoading ? <EmptyLoadingView /> : <EmptyView />}
					label={formatMessage(messages.componentsTableLabel)}
				/>
			</TableContainer>
		);
	}
}

const usePrevious = <T,>(value: T): T | undefined => {
	const ref = useRef<T>();
	useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref.current;
};

const defaultProps = {
	editable: false,
	isLoading: false,
	orderField: NAME,
	orderDirection: ASC,
	query: '',
};

const TableViewNew: React.FC<PropsNew> = ({
	isLoading = defaultProps.isLoading,
	componentIds,
	editable = defaultProps.editable,
	onSort,
	orderField = defaultProps.orderField,
	orderDirection = defaultProps.orderDirection,
	query = defaultProps.query,
}) => {
	const { formatMessage } = useIntl();
	const prevQuery = usePrevious(query);

	const shouldAnnounce: boolean = !isLoading && Boolean(query) && prevQuery !== query;

	const actionCellHeader = (
		<Stack as="span" testId="project-directories.components.table.action-cell-header-container">
			{formatMessage(messages.moreActions)}
		</Stack>
	);

	const headCells = [
		{
			key: NAME,
			content: formatMessage(messages.component),
			width: 20,
			isSortable: true,
		},
		{
			key: DESCRIPTION,
			content: formatMessage(messages.description),
			width: 30,
			isSortable: true,
		},
		{ key: LEAD, content: formatMessage(messages.lead), width: 20, isSortable: true },
		{ content: formatMessage(messages.defaultAssignee), width: 15 },
		{
			key: ISSUES,
			content: formatMessage(messages.issues),
			width: 10,
			isSortable: true,
		},
		{
			content: actionCellHeader,
			width: 6,
		},
	];

	const head = { cells: editable ? headCells : headCells.slice(0, headCells.length - 1) };
	const rowCells = componentIds.map((componentId, index) => {
		// the page is already sorted (by the API), so fake key,
		// so that DynamicTable won't destroy it
		const key = orderDirection === 'DESC' ? -(index + 1) : index + 1;
		const cells = [
			{
				content: (
					<ComponentCellContainer>
						<ComponentCell componentId={componentId} />
					</ComponentCellContainer>
				),
				key,
			},
			{ content: <DescriptionCell componentId={componentId} />, key },
			{ content: <LeadCell componentId={componentId} />, key },
			{ content: <DefaultAssigneeCell componentId={componentId} />, key },
			{ content: <IssueCountCell componentId={componentId} />, key },
		];
		if (editable) {
			// @ts-expect-error - TS2345 - Argument of type '{ content: JSX.Element; }' is not assignable to parameter of type '{ content: JSX.Element; key: number; }'.
			cells.push({ content: <ActionsCell componentId={componentId} /> });
		}
		return { cells };
	});

	return (
		<TableContainer data-testid="project-directories.components.table.table-container">
			{fg('mc-devops-proj-components-a11y') && (
				<AnnouncerV2
					message={formatMessage(messages.searchResults, {
						total: rowCells.length,
					})}
					shouldAnnounce={shouldAnnounce}
				/>
			)}
			<DynamicTableStateless
				isFixedSize
				head={head}
				rows={rowCells}
				onSort={onSort}
				sortKey={orderField}
				sortOrder={orderDirection}
				isLoading={isLoading}
				emptyView={isLoading ? <EmptyLoadingView /> : <EmptyView />}
				label={formatMessage(messages.componentsTableLabel)}
			/>
		</TableContainer>
	);
};

export default componentWithFG(
	'mc-devops-proj-components-a11y',
	TableViewNew,
	injectIntl(TableViewOld),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableContainer = styled2.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& tbody': {
		verticalAlign: 'top',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& td': {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& td:nth-of-type(1)': {
		wordWrap: 'break-word',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& td:nth-of-type(2)': {
		wordWrap: 'break-word',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& td:nth-of-type(3)': {
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& td:nth-of-type(6)': {
		paddingTop: token('space.050'),
		paddingBottom: token('space.0'),
		textAlign: 'right',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& th': {
		verticalAlign: 'middle',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:last-child': {
			textAlign: 'right',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ComponentCellContainer = styled2.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${gridSize * 13}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyLoadingView = styled2.div({
	height: '150px',
});
